import { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { updateSpecification, getSpecificationItem, type Specification } from '../../api/specification';
import { getDeviceTypesList } from '../../api/deviceTypesList';
import { getDeviceContacts, createDeviceContact, updateDeviceContact, deleteDeviceContact } from '../../api/connections';
// import { getSchemaTypes } from '../../api/schemaTypes'; // Legacy, не используется
import { getSchemas } from '../../api/schemas';
import { getElectricalPanels } from '../../api/electricalPanels';
import { useToast } from '../../hooks/useToast';

interface DeviceSpecEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDevice: Specification | null;
  projectId: number;
  onSuccess?: () => void; // Callback to trigger data reload
}

export default function DeviceSpecEditModal({ 
  isOpen, 
  onClose, 
  selectedDevice, 
  projectId,
  onSuccess 
}: DeviceSpecEditModalProps) {
  const toast = useToast();
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null);
  const [currentDocumentationUrl, setCurrentDocumentationUrl] = useState<string>('');
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>('');
  // Contacts management state (reused from Devices page)
  const [contacts, setContacts] = useState<any[]>([]);
  const [showContactManager, setShowContactManager] = useState(false);
  const [showContactEditor, setShowContactEditor] = useState(false);
  const [editingContact, setEditingContact] = useState<any | null>(null);
  const [mouseDownTargetContact, setMouseDownTargetContact] = useState<EventTarget | null>(null);
  const [mouseDownTargetEditor, setMouseDownTargetEditor] = useState<EventTarget | null>(null);
  const [newContact, setNewContact] = useState<{ 
    contact_number: number | string;
    contact_label: string;
    color_code: string;
    contact_position: 'top' | 'bottom' | 'left' | 'right';
    selected_schema_ids: number[]; // Массив ID конкретных схем щита
  }>({
    contact_number: 1,
    contact_label: '',
    color_code: '',
    contact_position: 'top',
    selected_schema_ids: [] // По умолчанию пусто
  });
  
  const queryClient = useQueryClient();

  // Load device types dynamically
  const { data: deviceTypes = [] } = useQuery({
    queryKey: ['deviceTypesList'],
    queryFn: getDeviceTypesList
  });

  // Load schema types for contact selection (legacy, для обратной совместимости)
  // const { data: schemaTypes = [], isLoading: schemaTypesLoading } = useQuery({
  //   queryKey: ['schema-types'],
  //   queryFn: getSchemaTypes
  // });

  // Load all panels for the project
  const { data: allPanels = [] } = useQuery({
    queryKey: ['electrical-panels', projectId],
    queryFn: () => getElectricalPanels(projectId),
    enabled: !!projectId
  });

  // Load schemas for all panels in the project
  const { data: allSchemas = [], isLoading: panelSchemasLoading } = useQuery({
    queryKey: ['schemas-all-panels', projectId],
    queryFn: async () => {
      if (!projectId || allPanels.length === 0) return []
      
      // Загружаем схемы для всех щитов проекта
      const schemasPromises = allPanels.map(panel => 
        getSchemas(projectId, panel.id).catch(err => {
          console.error(`Failed to load schemas for panel ${panel.id}:`, err)
          return []
        })
      )
      
      const schemasArrays = await Promise.all(schemasPromises)
      // Объединяем все схемы в один массив
      return schemasArrays.flat()
    },
    enabled: !!projectId && allPanels.length > 0
  });

  // Contact colors (copied from Devices page)
  const CONTACT_COLORS = [
    { value: '', label: 'Не указан', color: 'transparent' },
    { value: 'WH', label: 'WH - Белый', color: '#FFFFFF' },
    { value: 'BK', label: 'BK - Чёрный', color: '#000000' },
    { value: 'BN', label: 'BN - Коричневый', color: '#8B4513' },
    { value: 'BU', label: 'BU - Синий', color: '#0066CC' },
    { value: 'RD', label: 'RD - Красный', color: '#FF0000' },
    { value: 'YE', label: 'YE - Жёлтый', color: '#FFD700' },
    { value: 'GN', label: 'GN - Зелёный', color: '#008000' },
    { value: 'GNYE', label: 'GNYE - Жёлто-зелёный', color: '#FFD700', color2: '#008000' },
    { value: 'GY', label: 'GY - Серый', color: '#808080' },
    { value: 'OG', label: 'OG - Оранжевый', color: '#FFA500' },
    { value: 'VT', label: 'VT - Фиолетовый', color: '#8A2BE2' },
    { value: 'PK', label: 'PK - Розовый', color: '#FFC0CB' },
    // Цвета витой пары
    { value: 'WO', label: 'WO - Бело-оранжевый', color: '#FFFFFF', color2: '#FFA500' },
    { value: 'WG', label: 'WG - Бело-зелёный', color: '#FFFFFF', color2: '#008000' },
    { value: 'WB', label: 'WB - Бело-синий', color: '#FFFFFF', color2: '#0066CC' },
    { value: 'WN', label: 'WN - Бело-коричневый', color: '#FFFFFF', color2: '#8B4513' }
  ];

  // Helpers
  const loadDeviceContacts = async (specificationId: number) => {
    try {
      const deviceContacts = await getDeviceContacts(specificationId);
      const sorted = deviceContacts.sort((a: any, b: any) => {
        const na = parseInt(String(a.contact_number).split('-')[0]);
        const nb = parseInt(String(b.contact_number).split('-')[0]);
        return na - nb;
      });
      setContacts(sorted);
    } catch (e) {
      console.error('Failed to load contacts:', e);
      setContacts([]);
    }
  };

  const handleOpenContacts = () => {
    if (!selectedDevice) return;
    loadDeviceContacts(selectedDevice.id);
    setShowContactManager(true);
  };

  // Mutations for contacts
  const createContactMutation = useMutation({
    mutationFn: createDeviceContact
  });

  const updateContactMutation = useMutation({
    mutationFn: async (data: { id: number; updates: any }) => {
      await updateDeviceContact(data.id, data.updates);
    }
  });

  const deleteContactMutation = useMutation({
    mutationFn: deleteDeviceContact,
    onSuccess: () => {
      if (selectedDevice) loadDeviceContacts(selectedDevice.id);
    }
  });

  const closeAllContactModals = () => {
    setShowContactManager(false);
    setShowContactEditor(false);
    setEditingContact(null);
  };

  const closeContactEditor = () => {
    setShowContactEditor(false);
    setEditingContact(null);
  };

  const handleOpenNewContactEditor = () => {
    setEditingContact(null);
    setNewContact({ contact_number: 1, contact_label: '', color_code: '', contact_position: 'top', selected_schema_ids: [] });
    setShowContactEditor(true);
  };

  const handleEditContact = (contact: any) => {
    setEditingContact(contact);

    // Convert schema_ids (или schema_type_ids для обратной совместимости) в selected_schema_ids
    let selected_schema_ids: number[] = [];
    
    // Сначала проверяем новое поле schema_ids
    if (contact.schema_ids) {
      try {
        if (Array.isArray(contact.schema_ids)) {
          selected_schema_ids = contact.schema_ids;
        } else if (typeof contact.schema_ids === 'string') {
          selected_schema_ids = JSON.parse(contact.schema_ids);
        }
      } catch (error) {
        console.warn('Error parsing schema_ids:', error);
      }
    }
    
    // Fallback: если schema_ids нет, пытаемся получить схемы по schema_type_ids
    if (selected_schema_ids.length === 0 && contact.schema_type_ids && allSchemas.length > 0) {
      try {
        let schemaTypeIds: number[] = [];
        if (Array.isArray(contact.schema_type_ids)) {
          schemaTypeIds = contact.schema_type_ids;
        } else if (typeof contact.schema_type_ids === 'string') {
          schemaTypeIds = JSON.parse(contact.schema_type_ids);
        }
        
        // Находим все схемы с подходящими типами
        selected_schema_ids = allSchemas
          .filter(schema => schemaTypeIds.includes(schema.schema_type_id || 0))
          .map(schema => schema.id);
      } catch (error) {
        console.warn('Error parsing schema_type_ids:', error);
      }
    }

    setNewContact({
      contact_number: contact.contact_number,
      contact_label: contact.contact_label || '',
      color_code: contact.color_code || '',
      contact_position: (contact.contact_position || 'top') as 'top' | 'bottom' | 'left' | 'right',
      selected_schema_ids: selected_schema_ids
    });
    setShowContactEditor(true);
  };

  const handleDeleteContact = (contact: any) => {
    if (confirm(`Удалить контакт "${contact.contact_label || contact.contact_number}"?`)) {
      deleteContactMutation.mutate(contact.id);
    }
  };

  // Функция автосохранения схем (только для редактирования)
  const handleSchemaToggleAutoSave = async (schemaId: number) => {
    if (!selectedDevice || !editingContact) return;
    
    // Сохраняем текущее состояние для отката
    const previousIds = [...newContact.selected_schema_ids];
    
    const isSelected = previousIds.includes(schemaId);
    const updatedIds = isSelected
      ? previousIds.filter(id => id !== schemaId)
      : [...previousIds, schemaId];
    
    // Обновляем состояние сразу для UI
    setNewContact(prev => ({
      ...prev,
      selected_schema_ids: updatedIds
    }));
    
    // Сохраняем в БД
    const updates: any = {
      contact_number: newContact.contact_number,
      contact_label: newContact.contact_label || undefined,
      color_code: newContact.color_code || undefined,
      contact_position: newContact.contact_position,
      schema_ids: updatedIds
    };
    
    try {
      await updateContactMutation.mutateAsync({ id: editingContact.id, updates });
      await loadDeviceContacts(selectedDevice.id);
    } catch (error) {
      console.error('Error auto-saving schema selection:', error);
      // Откатываем состояние при ошибке
      setNewContact(prev => ({
        ...prev,
        selected_schema_ids: previousIds
      }));
    }
  };

  const handleCreateOrUpdateContact = async () => {
    if (!selectedDevice) return;
    // Отправляем schema_ids (массив ID конкретных схем)
    const schema_ids = newContact.selected_schema_ids || [];

    if (editingContact) {
      const updates: any = {
        contact_number: newContact.contact_number,
        contact_label: newContact.contact_label || undefined,
        color_code: newContact.color_code || undefined,
        contact_position: newContact.contact_position,
        schema_ids // Новое поле для конкретных схем
      };
      await updateContactMutation.mutateAsync({ id: editingContact.id, updates });
      await loadDeviceContacts(selectedDevice.id);
      closeContactEditor();
    } else {
      await createContactMutation.mutateAsync({
        specification_id: selectedDevice.id,
        contact_number: newContact.contact_number,
        contact_label: newContact.contact_label || undefined,
        color_code: newContact.color_code || undefined,
        contact_position: newContact.contact_position,
        schema_ids // Новое поле для конкретных схем
      });
      await loadDeviceContacts(selectedDevice.id);
      closeContactEditor();
    }
  };

  // Update selected device type when selectedDevice changes
  useEffect(() => {
    if (selectedDevice) {
      setSelectedDeviceType(selectedDevice.device_type_code || '');
      setCurrentDocumentationUrl(selectedDevice.documentation_url || '');
    }
  }, [selectedDevice]);

  // Initialize URL when modal opens
  useEffect(() => {
    if (selectedDevice) {
      setCurrentDocumentationUrl(selectedDevice.documentation_url || '');
    }
  }, [selectedDevice]);

  // Update device mutation
  const updateDeviceMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<Specification> }) => {
      await updateSpecification(data.id, data.updates);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['specification', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projectDevices', projectId] });
      // Инвалидируем данные конкретного устройства для страницы просмотра
      if (selectedDevice?.id) {
        const deviceId = selectedDevice.id
        // Загружаем обновленные данные и устанавливаем их в кэш для немедленного отображения
        try {
          const updatedDevice = await getSpecificationItem(deviceId);
          // Устанавливаем обновленные данные в кэш для всех возможных вариантов query keys
          queryClient.setQueryData(['device', deviceId], updatedDevice);
          queryClient.setQueryData(['device', String(deviceId)], updatedDevice);
          queryClient.setQueryData(['device', Number(deviceId)], updatedDevice);
        } catch (error) {
          console.error('Failed to fetch updated device:', error);
        }
        // Инвалидируем все возможные варианты query keys (число и строка)
        queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
        queryClient.invalidateQueries({ queryKey: ['device', String(deviceId)] });
        queryClient.invalidateQueries({ queryKey: ['device', Number(deviceId)] });
        // Также инвалидируем все queries, начинающиеся с 'device' (на случай других форматов)
        queryClient.invalidateQueries({ predicate: (query) => 
          query.queryKey[0] === 'device' && 
          (query.queryKey[1] === deviceId || query.queryKey[1] === String(deviceId) || query.queryKey[1] === Number(deviceId))
        });
        // Принудительно обновляем данные через refetch (await для гарантии обновления)
        await queryClient.refetchQueries({ queryKey: ['device', deviceId] });
        await queryClient.refetchQueries({ queryKey: ['device', String(deviceId)] });
      }
      toast.success('Устройство успешно обновлено');
      onClose();
      onSuccess?.(); // Call the onSuccess prop
    },
    onError: (error) => {
      console.error('Error updating device specification:', error);
    }
  });

  const handleEditDevice = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDevice) return;
    
    const formData = new FormData(e.currentTarget);
    
    // Map device type codes to IDs (since API doesn't return IDs)
    const deviceTypeIdMap: Record<string, number> = {
      'lighting': 1,
      'outlet': 2, 
      'switch': 3,
      'sensor': 4,
      'actuator': 5,
      'breaker': 6,
      'cable': 7,
      'terminal': 8,
      'other': 9,
      'breaker1': 10,
      'breaker2': 11,
      'Executive': 12,
      'Electricdrives': 13,
      'knxdev': 14
    };
    
    // Parse dimensions and convert to mm * 10 (stored in tenths of millimeters)
    const widthValue = formData.get('width') as string
    const heightValue = formData.get('height') as string
    const depthValue = formData.get('depth') as string
    
    const updates: Partial<Specification> = {
      name: formData.get('name') as string,
      description: formData.get('description') as string || undefined,
      model: formData.get('model') as string || undefined,
      manufacturer: formData.get('manufacturer') as string || undefined,
      power_consumption: parseFloat(formData.get('power_consumption') as string) || undefined,
      length_meters: parseFloat(formData.get('length_meters') as string) || undefined,
      width: widthValue ? Math.round(parseFloat(widthValue) * 10) : undefined,
      height: heightValue ? Math.round(parseFloat(heightValue) * 10) : undefined,
      depth: depthValue ? Math.round(parseFloat(depthValue) * 10) : undefined,
      visual_template: formData.get('visual_template') as string || undefined,
      documentation_url: currentDocumentationUrl || undefined,
      device_type_id: deviceTypeIdMap[selectedDeviceType] || selectedDevice.device_type_id
    };

    updateDeviceMutation.mutate({ id: selectedDevice.id, updates });
  };

  if (!isOpen || !selectedDevice) return null;

  return (<>
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={(e) => setMouseDownTarget(e.target)}
      onMouseUp={(e) => {
        if (e.target === mouseDownTarget && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">
          Изменить спецификацию устройства {selectedDevice ? `id=${selectedDevice.id}` : ''}
        </h3>
        {/* Contacts entry button */}
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={handleOpenContacts}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            ⚡ Контакты устройства
          </button>
        </div>
        <form onSubmit={handleEditDevice} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Тип устройства</label>
              <select
                name="device_type"
                value={selectedDeviceType}
                onChange={(e) => setSelectedDeviceType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {deviceTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Название</label>
              <input
                type="text"
                name="name"
                required
                defaultValue={selectedDevice.name}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Описание</label>
              <textarea
                name="description"
                defaultValue={selectedDevice.description || ''}
                placeholder="Описание спецификации устройства"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Модель</label>
              <input
                type="text"
                name="model"
                defaultValue={selectedDevice.model || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Производитель</label>
              <input
                type="text"
                name="manufacturer"
                defaultValue={selectedDevice.manufacturer || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>



            <div>
              <label className="block text-sm font-medium mb-1">Потребляемая мощность (Вт)</label>
              <input
                type="number"
                name="power_consumption"
                step="0.1"
                min="0"
                defaultValue={selectedDevice.power_consumption || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Длина (метры)</label>
              <input
                type="number"
                name="length_meters"
                step="0.1"
                min="0"
                defaultValue={selectedDevice.length_meters || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Ширина (мм)</label>
              <input
                type="number"
                name="width"
                step="0.1"
                min="0"
                defaultValue={selectedDevice.width ? (selectedDevice.width / 10) : ''}
                placeholder="напр: 17.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Высота (мм)</label>
              <input
                type="number"
                name="height"
                step="0.1"
                min="0"
                defaultValue={selectedDevice.height ? (selectedDevice.height / 10) : ''}
                placeholder="напр: 90"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Глубина (мм)</label>
              <input
                type="number"
                name="depth"
                step="0.1"
                min="0"
                defaultValue={selectedDevice.depth ? (selectedDevice.depth / 10) : ''}
                placeholder="напр: 60"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Визуальный шаблон</label>
            <input
              type="text"
              name="visual_template"
              defaultValue={selectedDevice.visual_template || ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">URL документации</label>
            <input
              type="url"
              name="documentation_url"
              value={currentDocumentationUrl}
              onChange={(e) => setCurrentDocumentationUrl(e.target.value)}
              placeholder="https://example.com/docs"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            {currentDocumentationUrl && (
              <div className="mt-2">
                <a
                  href={currentDocumentationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                >
                  📖 Открыть документацию
                  <span className="text-xs">(откроется в новом окне)</span>
                </a>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={updateDeviceMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {updateDeviceMutation.isPending ? 'Обновление...' : 'Обновить устройство'}
            </button>
          </div>
        </form>
      </div>
    </div>

    {/* Contact Management Modal */}
    {showContactManager && selectedDevice && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onMouseDown={(e) => setMouseDownTargetContact(e.target)}
        onMouseUp={(e) => {
          if (e.target === mouseDownTargetContact && e.target === e.currentTarget) {
            closeAllContactModals();
          }
          setMouseDownTargetContact(null);
        }}
      >
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">⚡ Контакты устройства "{selectedDevice.name}"</h3>
            <div className="flex items-center space-x-2">
              <button onClick={handleOpenNewContactEditor} className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700">➕ Добавить контакт</button>
              <button onClick={closeAllContactModals} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
          </div>
          <div className="space-y-3">
            {contacts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="mb-3">Контакты не созданы</p>
                <button onClick={handleOpenNewContactEditor} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm">Создать первый контакт</button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">№</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Маркировка</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Цвет</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Показать</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contacts.map((contact: any) => (
                      <tr key={contact.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-blue-600">#{contact.contact_number}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{contact.contact_label || `Контакт ${contact.contact_number}`}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{contact.color_code ? (CONTACT_COLORS.find(c => c.value === contact.color_code)?.label || contact.color_code) : 'Не указан'}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className="w-6 h-4 rounded border border-gray-400 relative overflow-hidden"
                              style={{
                                backgroundColor: (() => {
                                  const colorInfo = contact.color_code ? CONTACT_COLORS.find(c => c.value === contact.color_code) : null;
                                  return colorInfo?.color2 ? 'transparent' : (colorInfo?.color as string) || (contact.color_code ? '#ccc' : 'transparent');
                                })(),
                                backgroundImage: (() => {
                                  const colorInfo = contact.color_code ? CONTACT_COLORS.find(c => c.value === contact.color_code) : null;
                                  if (!contact.color_code) {
                                    return `linear-gradient(45deg, #ccc 25%, transparent 25%), 
                                            linear-gradient(-45deg, #ccc 25%, transparent 25%), 
                                            linear-gradient(45deg, transparent 75%, #ccc 75%), 
                                            linear-gradient(-45deg, transparent 75%, #ccc 75%)`;
                                  }
                                  if (colorInfo?.color2) {
                                    return `linear-gradient(135deg, ${colorInfo.color} 0%, ${colorInfo.color} 47%, rgba(0,0,0,0.2) 49%, rgba(0,0,0,0.2) 51%, ${colorInfo.color2} 53%, ${colorInfo.color2} 100%)`;
                                  }
                                  return 'none';
                                })(),
                                backgroundSize: !contact.color_code ? '4px 4px' : '100% 100%',
                                backgroundPosition: !contact.color_code ? '0 0, 0 2px, 2px -2px, -2px 0px' : 'center'
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex justify-end space-x-1">
                            <button onClick={() => handleEditContact(contact)} className="action-icon-btn edit" title="Редактировать">✏️</button>
                            <button onClick={() => handleDeleteContact(contact)} className="action-icon-btn delete" title="Удалить">🗑️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={closeAllContactModals} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Закрыть</button>
          </div>
        </div>
      </div>
    )}

    {/* Contact Editor Modal */}
    {showContactEditor && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onMouseDown={(e) => setMouseDownTargetEditor(e.target)}
        onMouseUp={(e) => {
          if (e.target === mouseDownTargetEditor && e.target === e.currentTarget) {
            closeContactEditor();
          }
          setMouseDownTargetEditor(null);
        }}
      >
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center p-6 border-b">
            <h3 className="text-lg font-medium text-gray-900">{editingContact ? 'Редактировать контакт' : 'Новый контакт'}</h3>
            <button onClick={closeContactEditor} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Номер контакта</label>
              <input
                type="text"
                value={newContact.contact_number}
                onChange={(e) => setNewContact(prev => ({ ...prev, contact_number: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateOrUpdateContact(); } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="1 или 1-TOP"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Маркировка</label>
              <input
                type="text"
                value={newContact.contact_label}
                onChange={(e) => setNewContact(prev => ({ ...prev, contact_label: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateOrUpdateContact(); } }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Li, Lo, N, PE"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">На каких схемах отображать</label>
              {panelSchemasLoading ? (
                <div className="text-sm text-gray-500 mt-2">Загрузка схем...</div>
              ) : allSchemas.length === 0 ? (
                <div className="text-sm text-gray-500 mt-2">У проекта нет схем</div>
              ) : (
                <div className="mt-2 space-y-4">
                  {allPanels.map(panel => {
                    // Фильтруем схемы для этого щита
                    const panelSchemas = allSchemas.filter(schema => schema.electrical_panel_id === panel.id)
                    
                    // Пропускаем щиты без схем
                    if (panelSchemas.length === 0) return null
                    
                    return (
                      <div key={panel.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="text-sm font-semibold text-gray-700 mb-2">
                          Щит {panel.name}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {panelSchemas.map(schema => {
                            const isSelected = newContact.selected_schema_ids.includes(schema.id);
                            return (
                              <button
                                key={schema.id}
                                type="button"
                                onClick={() => {
                                  if (editingContact) {
                                    handleSchemaToggleAutoSave(schema.id);
                                  } else {
                                    setNewContact(prev => {
                                      const isSelected = prev.selected_schema_ids.includes(schema.id);
                                      const updatedIds = isSelected
                                        ? prev.selected_schema_ids.filter(id => id !== schema.id)
                                        : [...prev.selected_schema_ids, schema.id];
                                      
                                      return {
                                        ...prev,
                                        selected_schema_ids: updatedIds
                                      };
                                    });
                                  }
                                }}
                                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md border transition-colors w-32 ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                                }`}
                              >
                                <span className="text-sm">{isSelected ? '✅' : '📋'}</span>
                                <span className="text-sm font-medium truncate">{schema.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {newContact.selected_schema_ids.length > 0 && (
                <div className="mt-3 text-sm text-gray-600">
                  Выбрано схем: {newContact.selected_schema_ids.length}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Расположение контакта</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['top','bottom','left','right'].map((pos) => (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => setNewContact(prev => ({ ...prev, contact_position: pos as any }))}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md border transition-colors ${newContact.contact_position === pos ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                  >
                    <span className="text-sm">{pos === 'top' ? '⬆️' : pos === 'bottom' ? '⬇️' : pos === 'left' ? '⬅️' : '➡️'}</span>
                    <span className="text-sm font-medium">{pos}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Цвет (необязательно)</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {CONTACT_COLORS.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setNewContact(prev => ({ ...prev, color_code: color.value }))}
                    className={`flex items-center space-x-2 p-2 rounded-md border transition-colors ${newContact.color_code === color.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                  >
                    <div
                      className="w-4 h-4 rounded border border-gray-400 flex-shrink-0 relative overflow-hidden"
                      style={{
                        backgroundColor: color.color2 ? 'transparent' : (typeof color.color === 'string' ? color.color : 'transparent'),
                        backgroundImage: color.value === ''
                          ? `linear-gradient(45deg, #ccc 25%, transparent 25%),
                              linear-gradient(-45deg, #ccc 25%, transparent 25%),
                              linear-gradient(45deg, transparent 75%, #ccc 75%),
                              linear-gradient(-45deg, transparent 75%, #ccc 75%)`
                          : color.color2
                            ? `linear-gradient(135deg, ${color.color} 0%, ${color.color} 47%, rgba(0,0,0,0.2) 49%, rgba(0,0,0,0.2) 51%, ${color.color2} 53%, ${color.color2} 100%)`
                            : 'none',
                        backgroundSize: color.value === '' ? '4px 4px' : '100% 100%',
                        backgroundPosition: color.value === '' ? '0 0, 0 2px, 2px -2px, -2px 0px' : 'center'
                      }}
                    />
                    <span className="text-xs text-gray-700">{color.value ? color.label : 'Нет'}</span>
                  </button>
                ))}
              </div>
              {newContact.color_code && (
                <div className="mt-2 text-sm text-gray-600">Выбран: <span className="font-medium">{CONTACT_COLORS.find(c => c.value === newContact.color_code)?.label || 'Не указан'}</span></div>
              )}
            </div>

          </div>

          <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end space-x-3">
            <button onClick={closeContactEditor} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Отмена</button>
            <button onClick={handleCreateOrUpdateContact} disabled={createContactMutation.isPending || updateContactMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {editingContact ? (updateContactMutation.isPending ? 'Сохранение...' : 'Сохранить') : (createContactMutation.isPending ? 'Создание...' : 'Создать')}
            </button>
          </div>
        </div>
      </div>
    )}
  </>);
} 